import { useCallback, useMemo } from 'react';
import Modal from '@ui/modal/modal';
import Button from '@ui/buttons/button/button';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Text from '@ui/text/text/text';
import Container from '@ui/container/container';
import SortButton from '@ui/buttons/sort-button/sort-button';
import SearchInput from '@ui/search-input/search-input';
import Paging from '@ui/paging/paging';
import OptionalComponent from '@ui/optional-component/optional-component';
import Table from '@layouts/layout-table/layout-table';
import TableHead from '@layouts/layout-table/layout-table-head';
import TableBody from '@layouts/layout-table/layout-table-body';
import TableRow from '@layouts/layout-table/layout-table-row';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { loadAudienceMembers, resetAudienceMembers } from '@/store/slices/audiences';
import {
  selectAudienceMembersLoading,
  selectAudienceMembersPage,
  selectAudienceMembersPageSize,
  selectAudienceMembersRegistrationStatus,
  selectAudienceMembersSearch,
  selectAudienceMembersSort,
  selectAudienceMembersSortDirection,
  selectAudienceMembersSource,
  selectAudienceMembersTotal,
  selectAudienceMembersUsers,
} from '@/store/slices/audiences/selectors';
import { selectUserChannel } from '@/store/slices/user/selectors';
import { isWebinarChannel } from '@/utils/project-types/project-type-guards';
import type { RegistrationFilter } from '@/types/all-attendees.types';
import { getDefaultColumnDefinitions } from '@/micros/edit-project/grid-areas/main/audiences/components/listsv2/profile-column-definitions';
import ProfileListCell from '@/micros/edit-project/grid-areas/main/audiences/components/listsv2/profile-list-cell';
import ProfilesRegistrationStatusFilter from '@/micros/edit-project/grid-areas/main/audiences/components/listsv2/profiles-registration-status-filter';
import { getColumnSortDirection } from '@/micros/edit-project/grid-areas/main/audiences/utils';
import DynamicAudienceChip from './dynamic-audience-chip';

const MODAL_BACKGROUND = 'var(--gray-900)';

/** Opened by dispatching loadAudienceMembers({ source }), closed by resetAudienceMembers() */
function AudienceMembersModal() {
  const dispatch = useAppDispatch();
  const audience = useAppSelector(selectAudienceMembersSource);
  const users = useAppSelector(selectAudienceMembersUsers);
  const loading = useAppSelector(selectAudienceMembersLoading);
  const total = useAppSelector(selectAudienceMembersTotal);
  const page = useAppSelector(selectAudienceMembersPage);
  const pageSize = useAppSelector(selectAudienceMembersPageSize);
  const search = useAppSelector(selectAudienceMembersSearch);
  const registrationStatus = useAppSelector(selectAudienceMembersRegistrationStatus);
  const sort = useAppSelector(selectAudienceMembersSort);
  const sortDirection = useAppSelector(selectAudienceMembersSortDirection);
  const userChannel = useAppSelector(selectUserChannel);

  // read-only view of the audience page table, limited to the default columns
  const visibleColumns = useMemo(
    () => getDefaultColumnDefinitions(isWebinarChannel(userChannel?.type)),
    [userChannel?.type]
  );

  const isDynamic = !!audience?.status;

  const onClose = useCallback(() => dispatch(resetAudienceMembers()), [dispatch]);

  const handleSort = (sortKey: string) => () => {
    const nextDirection = sort === sortKey && sortDirection === 'asc' ? 'desc' : 'asc';
    dispatch(loadAudienceMembers({ sort: { sort: sortKey, sortDirection: nextDirection } }));
  };

  const handleSearch = (value: string) => {
    dispatch(loadAudienceMembers({ search: value }));
  };

  const handleRegistrationStatusChange = (value: RegistrationFilter) => {
    dispatch(loadAudienceMembers({ registrationStatus: value }));
  };

  const handlePageChange = (nextPage: number) => {
    dispatch(loadAudienceMembers({ page: nextPage }));
  };

  const totalPages = Math.ceil(total / Number(pageSize));
  const isFiltered = !!search || registrationStatus !== 'all';

  const header = (
    <Container className="flex fd-column gap-24 padding-b-16" background={MODAL_BACKGROUND}>
      <Container className="flex ai-flex-start jc-space-between gap-16">
        <div className="flex fd-column gap-8">
          <div className="flex ai-center gap-12">
            <Text className="H4" design="GlobalIcoText1" clamp={1}>
              {audience?.label ?? ''}
            </Text>
            <OptionalComponent display={isDynamic}>
              <DynamicAudienceChip />
            </OptionalComponent>
          </div>
          <Text className="P2" design="GlobalIcoText3">
            {total.toLocaleString()} {total === 1 ? 'member' : 'members'}
          </Text>
        </div>
        <Button clear onClick={onClose} aria-label="Close">
          <Icon name={ICONS.CLOSE} size={16} color="var(--global-ico-text-2)" />
        </Button>
      </Container>

      <Container className="flex ai-center jc-space-between gap-16">
        <SearchInput
          placeholder="Search by name or email"
          onSubmit={handleSearch}
          onClear={() => handleSearch('')}
          debounceTime={500}
          isClearable
          isBorderless
          width="300px"
        />
        <OptionalComponent display={!isDynamic}>
          <ProfilesRegistrationStatusFilter
            id="audience-members-registration-status-filter"
            value={registrationStatus}
            onChange={handleRegistrationStatusChange}
          />
        </OptionalComponent>
      </Container>
    </Container>
  );

  const footer = (
    <OptionalComponent display={totalPages > 1}>
      <Container className="padding-t-16" background={MODAL_BACKGROUND}>
        <Paging page={page} totalPages={totalPages} setPage={handlePageChange} loading={loading} />
      </Container>
    </OptionalComponent>
  );

  return (
    <Modal
      open={!!audience}
      onRequestClose={onClose}
      showBackdrop
      focusLockProps={{ autoFocus: false }}
      customSize={{
        width: '900px',
        height: '640px',
        maxHeight: 'calc(100% - 32px)',
        maxWidth: 'calc(100% - 32px)',
      }}
      contentWrapperClassName="padding-h-40 padding-v-24 flex-column"
      backgroundColor={MODAL_BACKGROUND}
      RenderHeaderContent={header}
      RenderFooterContent={footer}
      data-component="AudienceMembersModal"
    >
      {!loading && !users.length ? (
        <Container className="flex ai-center jc-center padding-v-32">
          <Text className="P2" design="GlobalIcoText3">
            {isFiltered ? 'No members match your filters.' : "This audience doesn't have any members yet."}
          </Text>
        </Container>
      ) : (
        <Container opacity={loading ? 0.6 : 1} inert={loading}>
          <Table loading={loading}>
            <TableHead style={{ position: 'sticky', top: 0, backgroundColor: MODAL_BACKGROUND, zIndex: 2 }}>
              <TableRow>
                {visibleColumns.map(({ id, label, sortKey, columnWidth }) => (
                  <th key={id} style={{ width: columnWidth, minWidth: columnWidth }}>
                    <div className="flex ai-center gap-4">
                      <span>{label}</span>
                      {sortKey && (
                        <SortButton
                          onClick={handleSort(sortKey)}
                          sortDirection={getColumnSortDirection(sortKey, sort, sortDirection)}
                        />
                      )}
                    </div>
                  </th>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={`${user.bl_profile}-${user.email}`} hoverBackgroundColor="var(--global-cont-1)">
                  {visibleColumns.map((column) => (
                    <ProfileListCell key={column.id} column={column} user={user} />
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Container>
      )}
    </Modal>
  );
}

export default AudienceMembersModal;
